/**
 * Ряд инцидентов из снимка отчёта → вход для `IncidentsFlow`.
 *
 * Две стороны считают одно и то же по-разному. API отдаёт накопительный ряд,
 * в котором каждая точка уже включает всё, что было найдено до неё, вместе с
 * базой на начало периода. График принимает подневные приросты и накапливает
 * их сам — заодно считая по ним то, чего в накопительном ряде не видно:
 * среднее время ожидания и долю разобранного старого долга.
 *
 * Перевод обратим: сумма приростов равна `totalFound` и `totalFixed`, а
 * `openingBacklog` — разности базовых величин. Поэтому переходник, а не
 * правка контракта одной из сторон: обе формы верные, просто отвечают на
 * разные вопросы.
 *
 * Даты проходят насквозь в ISO: человеческий вид им придаёт ось графика в
 * момент показа.
 *
 * @format
 */

import type {IncidentDay} from '../ExecutiveReportChart/incidentsFlowMetrics';
import {toIsoDate, toLocalDate} from './helpers';
import type {Incidents, IncidentPoint} from './types';

export interface IncidentsFlowInput {
  days: IncidentDay[];
  /** Сколько ошибок было открыто на начало периода. */
  openingBacklog: number;
}

/**
 * Потолок длины календаря — год с запасом.
 *
 * Испорченная дата в ряду растянула бы перебор на столетия и повесила вкладку
 * ещё до первой отрисовки. Отчёты месячные, так что упереться в потолок можно
 * только на битых данных.
 */
const MAX_SPAN_DAYS = 400;

/**
 * Ряд по календарю: на каждый день периода ровно одна точка.
 *
 * График меряет время позициями в массиве — средний срок ожидания это
 * расстояние между индексами, а не разница дат. Пока день равен элементу,
 * счёт верен; выпавший из ряда день молча укоротил бы срок в отчёте.
 * Сплошного ряда контракт не обещает, поэтому достраиваем сами: день без
 * событий — это нулевой прирост, а не отсутствующая точка.
 *
 * Побочно снимается вопрос порядка: календарь расставляет дни сам, каким бы
 * ни пришёл исходный ряд.
 *
 * Возвращает `null`, когда достроить нельзя — непарсящаяся дата или
 * неправдоподобный размах. Тогда работаем с тем, что прислали.
 */
function byCalendar(cumulative: IncidentPoint[]): IncidentPoint[] | null {
  const byDate = new Map<string, IncidentPoint>();
  let min: string | null = null;
  let max: string | null = null;

  for (const point of cumulative) {
    if (!toLocalDate(point.date)) return null;
    byDate.set(point.date, point);
    if (min === null || point.date < min) min = point.date;
    if (max === null || point.date > max) max = point.date;
  }

  if (min === null || max === null) return null;

  const cursor = toLocalDate(min);
  const last = toLocalDate(max);
  if (!cursor || !last) return null;

  const filled: IncidentPoint[] = [];
  // Последние известные значения: в день без событий накопительный итог
  // остаётся прежним, поэтому прирост выходит нулевым сам собой.
  let carried: IncidentPoint | null = null;

  while (cursor <= last) {
    if (filled.length >= MAX_SPAN_DAYS) return null;

    const iso = toIsoDate(cursor);
    const point = byDate.get(iso);
    carried = point ?? (carried ? {...carried, date: iso} : null);
    if (carried) filled.push(carried);

    // Шаг через `setDate`, а не прибавлением суток в миллисекундах: в день
    // перевода часов их 23 или 25, и арифметика на 86 400 000 промахнулась бы
    // мимо календарной границы.
    cursor.setDate(cursor.getDate() + 1);
  }

  return filled;
}

export function toIncidentsFlowInput(incidents: Incidents): IncidentsFlowInput {
  const {baselineFound, baselineFixed, cumulative} = incidents;

  const series = byCalendar(cumulative) ?? cumulative;

  let prevFound = baselineFound;
  let prevFixed = baselineFixed;

  const days = series.map(point => {
    // Ряд приходит с сервера: убывающее накопительное значение означало бы
    // сбой на той стороне, а отрицательный прирост нарисовался бы слоем ниже
    // нуля. Обрезаем, чтобы график остался читаемым.
    const found = Math.max(0, point.found - prevFound);
    const fixed = Math.max(0, point.fixed - prevFixed);

    prevFound = point.found;
    prevFixed = point.fixed;

    return {date: point.date, new: found, fixed};
  });

  return {days, openingBacklog: Math.max(0, baselineFound - baselineFixed)};
}
